import { debounce, throttle, memoize } from 'lodash-es';

/**
 * Performance Optimization Utilities
 * Provides debouncing, throttling, caching, and lazy loading helpers
 */

export const performanceUtils = {
  // Debounce function (delays execution until after wait time)
  debounce(func, wait = 300) {
    return debounce(func, wait);
  },

  // Throttle function (limits execution to once per wait time)
  throttle(func, wait = 300) {
    return throttle(func, wait);
  },

  // Memoize expensive computations
  memoize(func, resolver = null) {
    return memoize(func, resolver);
  },

  // Simple in-memory cache with TTL
  createCache(ttl = 5 * 60 * 1000) {
    const cache = new Map();
    
    return {
      get(key) {
        const item = cache?.get(key);
        if (!item) return null;
        
        if (Date.now() > item?.expiry) {
          cache?.delete(key);
          return null;
        }
        
        return item?.value;
      },
      
      set(key, value) {
        cache?.set(key, {
          value,
          expiry: Date.now() + ttl
        });
      },
      
      clear() {
        cache?.clear();
      },
      
      delete(key) {
        cache?.delete(key);
      },
      
      has(key) {
        const item = cache?.get(key);
        if (!item) return false;
        if (Date.now() > item?.expiry) {
          cache?.delete(key);
          return false;
        }
        return true;
      }
    };
  },

  // Lazy load images with intersection observer
  lazyLoadImage(imgElement, src) {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            imgElement.src = src;
            observer.unobserve(imgElement);
          }
        });
      });
      observer?.observe(imgElement);
    } else {
      // Fallback for browsers without IntersectionObserver
      imgElement.src = src;
    }
  },

  // Batch multiple API calls
  async batchRequests(requests, batchSize = 5) {
    const results = [];
    for (let i = 0; i < requests?.length; i += batchSize) {
      const batch = requests?.slice(i, i + batchSize);
      const batchResults = await Promise.allSettled(batch);
      results?.push(...batchResults);
    }
    return results;
  },

  // Abort controller for cancellable requests
  createAbortController() {
    return new AbortController();
  },

  // Timeout promise
  timeout(ms, promise) {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Request timeout after ${ms}ms`)), ms)
      )
    ]);
  }
};

// Global cache instances
export const dataCache = performanceUtils?.createCache(5 * 60 * 1000); // 5 minutes
export const imageCache = performanceUtils?.createCache(30 * 60 * 1000); // 30 minutes

export default performanceUtils;